import { Modal, Form, Input, Select, notification, Switch } from "antd";
import {
  MachineItem,
  ProductCardItem,
  useCreateTaskMutation,
  useGetAllProductCardsQuery,
  useUploadFileMutation,
  useUploadImageMutation,
} from "shared/api";
import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useTranslation } from "react-i18next";
import {
  TaskIconUploader,
  PeriodicitySection,
  AddAssociationsSection,
} from "./components";

dayjs.extend(utc);
dayjs.extend(timezone);

interface AddTaskModalProps {
  open: boolean;
  onClose: () => void;
  transformedUsers: {
    label: string;
    value: number | null | undefined;
  }[];
  machinesData: MachineItem[] | undefined;
  refetchExecutions: () => void | undefined;
  suppliersCompaniesFormatted:
    | {
        label: string;
        value: number;
      }[]
    | undefined;
}

export const AddTaskModal: React.FC<AddTaskModalProps> = ({
  open,
  onClose,
  transformedUsers,
  machinesData,
  refetchExecutions,
  suppliersCompaniesFormatted,
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [api, contextHolder] = notification.useNotification();
  const fileDocRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadedIconFile, setUploadedIconFile] = useState<File | null>(null);
  const [showDescription, setShowDescription] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [repeatEvery, setRepeatEvery] = useState<string | null>(null);

  const periodicity = Form.useWatch("periodicity", form);
  const isRecurrent = Form.useWatch("is_recurrent", form);
  const selectedTime = Form.useWatch("time", form);
  const enabled = Form.useWatch("enabled", form);

  useEffect(() => {
    setRepeatEvery(periodicity || null);
  }, [periodicity]);

  const [uploadFile] = useUploadFileMutation();
  const [uploadImage] = useUploadImageMutation();
  const [createTask, { isLoading }] = useCreateTaskMutation();

  const { data: productCardsData } = useGetAllProductCardsQuery({}) as {
    data: ProductCardItem[] | undefined;
  };

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setFileName(null);
      setPreviewUrl(null);
      setUploadedIconFile(null);
      setAttachedFiles([]);
    }
  }, [open, form]);

  const handleAttachFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (!files) return;
    setAttachedFiles((prev) => [...prev, ...Array.from(files)]);
  };

  const handleFinish = async (values: any) => {
    try {
      const payload = {
        name: values.name,
        periodicity: values.periodicity || null,
        is_recurrent: values.is_recurrent || false,
        time: values.time ? values.time.format("HH:mm:ss") : null,
        start_periodicity: values.start_periodicity
          ? dayjs(values.start_periodicity)
              .utc()
              .format("YYYY-MM-DDTHH:mm:ss[Z]")
          : null,
        end_periodicity: values.end_periodicity
          ? dayjs(values.end_periodicity).utc().format("YYYY-MM-DDTHH:mm:ss[Z]")
          : null,
        machines: values.machines || [],
        product_cards: values.product_cards || [],
        enabled: values.enabled,
        priority: values.priority,
        users: values.user || [],
        description: showDescription ? values.description : null,
        companies: values.companies,
      };

      const { id: taskId } = await createTask(payload).unwrap();

      if (uploadedIconFile && taskId) {
        await uploadImage({ id: taskId, file: uploadedIconFile }).unwrap();
      }

      if (attachedFiles.length > 0 && taskId) {
        await Promise.all(
          attachedFiles.map((file) => uploadFile({ id: taskId, file }).unwrap())
        );
      }

      refetchExecutions();

      api.success({
        message: "Task Created",
        description: "The task was successfully created.",
        placement: "topRight",
      });

      setShowDescription(false);
      setAttachedFiles([]);
      onClose();
    } catch (err) {
      api.error({
        message: "Task Creation Failed",
        description: "Something went wrong while creating the task.",
        placement: "topRight",
      });

      // eslint-disable-next-line no-console
      console.error("Failed to create task", err);
    } finally {
      onClose();
    }
  };

  const handleIconUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      setPreviewUrl(base64);
      setUploadedIconFile(file);
      setFileName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const handleIconRemove = () => {
    setPreviewUrl(null);
    setUploadedIconFile(null);
    setFileName(null);
  };

  const handleRemoveFile = (index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const themeColor = JSON.parse(
    localStorage.getItem("color-theme") || '"#002F5F"'
  );

  return (
    <>
      {contextHolder}
      <Modal
        open={open}
        onCancel={onClose}
        onOk={() => form.submit()}
        confirmLoading={isLoading}
        destroyOnClose
        title={
          <div className="flex items-center justify-between w-[95%] mb-3">
            <span>{t("taskHub.modal.addTask") || ""}</span>
            <div className="flex gap-2 items-center min-w-[130px] justify-end">
              <span className="text-sm">
                {enabled ? "Enabled" : "Disabled"}
              </span>
              <Switch
                checked={enabled}
                onChange={(checked) => form.setFieldValue("enabled", checked)}
                style={{
                  backgroundColor: enabled ? "limegreen" : "tomato",
                }}
              />
            </div>
          </div>
        }
        width={720}
        okButtonProps={{
          style: {
            backgroundColor: themeColor,
            color: "#fff",
            borderColor: "#1677ff",
          },
        }}
        okText={t("taskHub.modal.ok") || ""}
        cancelText={t("taskHub.modal.cancel") || ""}
      >
        <Form
          initialValues={{
            enabled: true,
          }}
          form={form}
          layout="vertical"
          onFinish={handleFinish}
        >
          <div>
            <div className="grid grid-cols-2 gap-x-4">
              <div className="col-span-2 flex items-center justify-between">
                <h3 className="text-base font-semibold mb-2">
                  {t("taskHub.modal.taskIcon") || ""}
                </h3>
                <TaskIconUploader
                  previewUrl={previewUrl}
                  fileName={fileName}
                  uploadedIconFile={uploadedIconFile}
                  onUpload={handleIconUpload}
                  onRemove={handleIconRemove}
                />
              </div>

              <Form.Item
                name="name"
                label={t("taskHub.modal.name") || ""}
                rules={[{ required: true }]}
              >
                <Input placeholder={t("taskHub.modal.enterTaskName") || ""} />
              </Form.Item>

              <Form.Item name="user" label={t("taskHub.modal.user") || ""}>
                <Select
                  mode="multiple"
                  showSearch
                  options={transformedUsers}
                  optionFilterProp="label"
                  placeholder={t("taskHub.modal.selectUser") || ""}
                  filterOption={(input, option) =>
                    (option?.label as string)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </div>
          </div>

          <PeriodicitySection
            isRecurrent={isRecurrent}
            repeatEvery={repeatEvery}
            selectedTime={selectedTime}
            form={form}
          />

          <AddAssociationsSection
            machinesData={machinesData}
            productCardsData={productCardsData}
            suppliersCompaniesFormatted={suppliersCompaniesFormatted}
            showDescription={showDescription}
            attachedFiles={attachedFiles}
            themeColor={themeColor}
            fileDocRef={fileDocRef}
            onToggleDescription={() => setShowDescription((prev) => !prev)}
            onAttachFiles={handleAttachFiles}
            onRemoveFile={handleRemoveFile}
          />

          <Form.Item name="enabled" noStyle>
            <Input type="hidden" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
